// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Web session handling.
 *
 * The access token lives only in memory. In browsers the refresh token is an HttpOnly cookie
 * that page scripts cannot read: requests marked `X-Granite-Client: web` get it as a cookie
 * instead of in the JSON body. In the desktop app it is kept in the OS keychain (P4-T04). Refresh tokens are single-use and reuse ends the sign-in, so refreshes are
 * serialized within the tab (one in flight) and across tabs (a Web Lock).
 */
import { ApiError, createGraniteClient, unwrap, type components } from "@granite/api-client";
import { createStore } from "zustand/vanilla";

import { isDesktop } from "./platform.js";

export type User = components["schemas"]["User"];
export type MFAChallenge = components["schemas"]["MFAChallenge"];
type TokenResponse = components["schemas"]["TokenResponse"];

export type SessionState =
  | { status: "loading" }
  | { status: "signedOut" }
  | { status: "signedIn"; user: User; accessToken: string; expiresAt: number }
  /** The server cannot be reached, but this browser was signed in: local vaults stay usable. */
  | { status: "offline"; user: User };

/** Signed in, or signed in earlier and now offline. */
export function hasUser(s: SessionState): s is Extract<SessionState, { user: User }> {
  return s.status === "signedIn" || s.status === "offline";
}

export const sessionStore = createStore<SessionState>(() => ({ status: "loading" }));

export const apiBaseUrl = import.meta.env.VITE_API_URL ?? window.location.origin;

export const webClientOptions = {
  baseUrl: apiBaseUrl,
  headers: isDesktop ? {} : { "X-Granite-Client": "web" },
  credentials: "include",
} as const;

/** Desktop: the refresh token, read from and written to the keychain. */
async function storedRefreshToken(): Promise<string> {
  if (!isDesktop) return "";
  const { secretGet } = await import("../platform/secrets.js");
  return (await secretGet("refresh-token")) ?? "";
}

async function storeRefreshToken(token: string | null): Promise<void> {
  if (!isDesktop) return;
  const { secretDelete, secretSet } = await import("../platform/secrets.js");
  if (token) await secretSet("refresh-token", token);
  else await secretDelete("refresh-token");
}

// Auth calls never carry the bearer token automatically, so this module has no import cycle
// with the main API client.
const authClient = createGraniteClient(webClientOptions);

/** Refresh this long before the access token expires. */
const refreshMargin = 30_000;
export const deviceKey = "granite.deviceId";
const channel = typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel("granite-session");

export function storageGet(key: string): string | undefined {
  try {
    return localStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
}

function storageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the server creates a new device record next time.
  }
}

function deviceInfo(): components["schemas"]["DeviceInfo"] {
  const id = storageGet(deviceKey);
  return { platform: "web", name: browserName(), ...(id ? { id } : {}) };
}

export function browserName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const os = /Mac OS X/.test(ua)
    ? "macOS"
    : /Windows/.test(ua)
      ? "Windows"
      : /Android/.test(ua)
        ? "Android"
        : /iPhone|iPad/.test(ua)
          ? "iOS"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return os ? `${browser} on ${os}` : browser;
}

const userKey = "granite.user";

function cachedUser(): User | undefined {
  const raw = storageGet(userKey);
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as User;
  } catch {
    return undefined;
  }
}

function accept(tokens: TokenResponse): void {
  storageSet(deviceKey, tokens.device_id);
  storageSet(userKey, JSON.stringify(tokens.user));
  sessionStore.setState(
    {
      status: "signedIn",
      user: tokens.user,
      accessToken: tokens.access_token,
      expiresAt: Date.now() + tokens.expires_in * 1000,
    },
    true,
  );
}

function signedOut(): void {
  try {
    localStorage.removeItem(userKey);
  } catch {
    // ignore
  }
  sessionStore.setState({ status: "signedOut" }, true);
}

/** Deletes every vault's local copy in this browser (on sign-out). */
export async function clearLocalVaults(): Promise<void> {
  if (typeof indexedDB === "undefined" || !("databases" in indexedDB)) return;
  const dbs = await indexedDB.databases();
  await Promise.all(
    dbs
      .filter((d) => d.name?.startsWith("granite-vault-"))
      .map(
        (d) =>
          new Promise<void>((resolve) => {
            const r = indexedDB.deleteDatabase(d.name ?? "");
            r.onsuccess = r.onerror = r.onblocked = () => {
              resolve();
            };
          }),
      ),
  );
}

async function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if ("locks" in navigator) {
    return navigator.locks.request("granite-session-refresh", fn);
  }
  return fn();
}

let refreshing: Promise<boolean> | undefined;

/**
 * Exchanges the refresh cookie for a new access token. Resolves false (and signs out) when
 * the session has ended. Concurrent callers share one request.
 */
export function refresh(): Promise<boolean> {
  refreshing ??= withRefreshLock(async () => {
    let stored: string;
    try {
      stored = await storedRefreshToken();
    } catch (err) {
      // The keychain cannot be read (locked, or access refused). That is no network trouble:
      // ask for a sign-in, but keep the item, which may work again once it is unlocked.
      console.warn("refresh token unreadable", err);
      signedOut();
      return false;
    }
    if (isDesktop && !stored) {
      signedOut();
      return false;
    }
    try {
      const tokens = unwrap(await authClient.POST("/v1/auth/refresh", { body: { refresh_token: stored } }));
      // Rotate in the keychain before anyone can refresh again (tokens work once).
      await storeRefreshToken(tokens.refresh_token);
      accept(tokens);
      return true;
    } catch (err) {
      const status = sessionStore.getState().status;
      // The server refused the token: the session has ended. A busy or failing server (429,
      // 5xx) has not ended it, and must not throw away a token that still works.
      if (err instanceof ApiError && err.status < 500 && err.status !== 429) {
        signedOut();
        void storeRefreshToken(null);
        void clearLocalVaults();
      } else if (status !== "signedIn") {
        // The server cannot be reached or answer. A browser that was signed in keeps working offline.
        const user = cachedUser();
        if (user) sessionStore.setState({ status: "offline", user }, true);
        else signedOut();
      }
      return false;
    }
  }).finally(() => {
    refreshing = undefined;
  });
  return refreshing;
}

/** Restores the session from the refresh cookie. Call once before the first render. */
export async function initSession(): Promise<void> {
  await refresh();
}

/** The current access token, refreshed first when it is about to expire. */
export async function accessToken(): Promise<string | undefined> {
  let s = sessionStore.getState();
  if (s.status !== "signedIn") return undefined;
  if (s.expiresAt - Date.now() < refreshMargin) {
    await refresh();
    s = sessionStore.getState();
    if (s.status !== "signedIn") return undefined;
  }
  return s.accessToken;
}

export type LoginResult = { kind: "signedIn" } | { kind: "mfa"; challenge: MFAChallenge };

/** Checks the password. Accounts with two-step verification return a challenge to finish. */
export async function login(email: string, password: string): Promise<LoginResult> {
  const data = unwrap(await authClient.POST("/v1/auth/login", { body: { email, password, device: deviceInfo() } }));
  if ("mfa_token" in data) return { kind: "mfa", challenge: data };
  await storeRefreshToken(data.refresh_token);
  accept(data);
  channel?.postMessage("signed-in");
  return { kind: "signedIn" };
}

/** What a "Continue with Google/Apple" sign-in sends besides the ID token (P1-T02). */
interface SocialLogin {
  idToken: string;
  /** The raw nonce; the provider saw its hash. */
  nonce: string;
  /** Signing up for a business account: the workspace a new account gets. */
  workspaceName?: string | undefined;
  /** Apple: the name it gives on the first sign-in only. */
  name?: string | undefined;
  /** Apple: the authorization code, for revoking access when the account is deleted. */
  code?: string | undefined;
}

/** Signs in (or up) with an ID token from Google or Apple, bound to the attempt's nonce. */
async function loginWithProvider(provider: "google" | "apple", login: SocialLogin): Promise<LoginResult> {
  const data = unwrap(
    await authClient.POST("/v1/auth/oidc/{provider}", {
      params: { path: { provider } },
      body: {
        id_token: login.idToken,
        nonce: login.nonce,
        device: deviceInfo(),
        ...(login.workspaceName ? { workspace_name: login.workspaceName } : {}),
        ...(login.name ? { name: login.name } : {}),
        ...(login.code ? { authorization_code: login.code } : {}),
      },
    }),
  );
  if ("mfa_token" in data) return { kind: "mfa", challenge: data };
  await storeRefreshToken(data.refresh_token);
  accept(data);
  channel?.postMessage("signed-in");
  return { kind: "signedIn" };
}

/** Signs in (or up) with a Google ID token bound to nonce (the raw value; Google saw its hash). */
export function loginWithGoogle(idToken: string, nonce: string, workspaceName?: string): Promise<LoginResult> {
  return loginWithProvider("google", { idToken, nonce, workspaceName });
}

/**
 * Signs in (or up) with an Apple ID token bound to nonce, with the authorization code and,
 * on the first sign-in, the name Apple sent along.
 */
export function loginWithApple(
  idToken: string,
  nonce: string,
  code?: string,
  name?: string,
  workspaceName?: string,
): Promise<LoginResult> {
  return loginWithProvider("apple", { idToken, nonce, code, name, workspaceName });
}

/** Finishes a two-step sign-in with an authenticator code or a recovery code. */
export async function loginSecondFactor(
  mfaToken: string,
  factor: { code: string } | { recovery_code: string },
): Promise<void> {
  const tokens = unwrap(await authClient.POST("/v1/auth/login/2fa", { body: { mfa_token: mfaToken, ...factor } }));
  await storeRefreshToken(tokens.refresh_token);
  accept(tokens);
  channel?.postMessage("signed-in");
}

/** Starts single sign-on for a work email; returns the identity provider's sign-in URL (P8-T05). */
export async function startSSO(email: string): Promise<string> {
  return unwrap(await authClient.POST("/v1/auth/sso/start", { body: { email } })).authorize_url;
}

/** Finishes single sign-on with what the identity provider sent back: the OpenID Connect
 * state and code, or the handoff code of a SAML sign-in. */
export async function finishSSO(from: { state: string; code: string } | { handoff: string }): Promise<void> {
  const tokens = unwrap(await authClient.POST("/v1/auth/sso/finish", { body: { ...from, device: deviceInfo() } }));
  await storeRefreshToken(tokens.refresh_token);
  accept(tokens);
  channel?.postMessage("signed-in");
}

/** Desktop: redeems the one-time code the web app handed over, with this app's PKCE verifier. */
export async function finishAppHandoff(code: string, verifier: string): Promise<void> {
  const tokens = unwrap(
    await authClient.POST("/v1/auth/app-handoff/finish", { body: { code, code_verifier: verifier, device: deviceInfo() } }),
  );
  await storeRefreshToken(tokens.refresh_token);
  accept(tokens);
  channel?.postMessage("signed-in");
}

/** Ends this browser's session on the server and in every open tab. */
export async function logout(): Promise<void> {
  const token = await accessToken();
  if (token) {
    try {
      await authClient.POST("/v1/auth/logout", { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      // Signing out locally still happens; the server session expires on its own.
    }
  }
  signedOut();
  await storeRefreshToken(null);
  await clearLocalVaults();
  try {
    localStorage.removeItem("granite.vaults");
  } catch {
    // storage blocked
  }
  channel?.postMessage("signed-out");
}

/** Forgets the session in this tab without calling the server (after a password reset). */
export function forgetSession(): void {
  signedOut();
  void storeRefreshToken(null);
  void clearLocalVaults();
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    if (sessionStore.getState().status === "offline") void refresh();
  });
}

channel?.addEventListener("message", (e: MessageEvent) => {
  if (e.data === "signed-out") signedOut();
  if (e.data === "signed-in" && sessionStore.getState().status !== "signedIn") void refresh();
});

export { authClient };
